import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export class CameraManager {
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    controls: OrbitControls;
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;

    private previousCameraPosition: THREE.Vector3;
    private previousControlsTarget: THREE.Vector3;
    private previousCamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
    private initialFrustumSize: number | null = null;

    constructor(scene: THREE.Scene, renderer: THREE.WebGLRenderer, container: HTMLElement) {
        this.scene = scene;
        this.renderer = renderer;
        this.setupCamera(container);
        this.setupControls(container);
    }

    private setupCamera(container: HTMLElement) {
        this.camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 1, 20000);
        this.camera.position.set(1500, 2000, 2500);
        this.camera.up.set(0, 0, 1);
    }

    private setupControls(container: HTMLElement) {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.05;
        this.controls.enableRotate = true;
        this.controls.enablePan = false;
        this.controls.enableZoom = false;

        // Improve touch handling for mobile devices
        const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (isMobile) {
            // Set appropriate touch rotation sensitivity
            this.controls.rotateSpeed = 0.5;

            // Enhance performance during touch interactions
            this.controls.enableDamping = false; // Disable damping on mobile for better performance

            // Better touch response
            this.controls.touches = {
                ONE: THREE.TOUCH.ROTATE,
                TWO: THREE.TOUCH.DOLLY_PAN
            };

            // Enable touch zoom for direct handling
            this.controls.enableZoom = true;
            this.controls.zoomSpeed = 1.0;

            // Prevent default touch actions when interacting with the canvas
            this.renderer.domElement.style.touchAction = 'none';
        }
    }

    setIsometricView(initial: boolean = false, project?: any, viewportGizmo?: any) {
        // Store previous camera state
        this.previousCamera = this.camera;
        this.previousCameraPosition = this.camera.position.clone();
        this.previousControlsTarget = this.controls.target.clone();

        const boundingBox = new THREE.Box3().setFromObject(this.scene);
        const center = boundingBox.getCenter(new THREE.Vector3());
        const size = boundingBox.getSize(new THREE.Vector3());

        const maxDim = Math.max(size.x, size.y, size.z);
        let distance;

        if (this.camera instanceof THREE.PerspectiveCamera) {
            const fov = this.camera.fov * (Math.PI / 180);
            distance = maxDim / (2 * Math.tan(fov / 2));
        } else {
            distance = maxDim;
        }

        distance *= 1.5;

        const aspect = this.renderer.domElement.width / this.renderer.domElement.height;
        const frustumSize = maxDim * 1.5 / 100;

        // Adjust near plane to be further from camera to prevent clipping
        const nearPlane = -distance; // Use negative distance for near plane
        const farPlane = distance * 3;

        this.camera = new THREE.OrthographicCamera(
            frustumSize * aspect / -2,
            frustumSize * aspect / 2,
            frustumSize / 2,
            frustumSize / -2,
            nearPlane,
            farPlane
        );

        // Set camera position and orientation
        if (initial && project?.camera) {
            const cameraData = JSON.parse(project.camera);
            this.camera.matrix.fromArray(cameraData);
            this.camera.matrix.decompose(this.camera.position, this.camera.quaternion, this.camera.scale);

            // Restore zoom from project if available, otherwise use default zoom
            if (project.camerazoom !== undefined) {
                this.camera.zoom = project.camerazoom;
            } else {
                this.camera.zoom = 1;
            }
            this.camera.updateProjectionMatrix();
        } else {
            this.camera.position.copy(this.previousCameraPosition);
            this.camera.lookAt(this.previousControlsTarget);

            // Maintain zoom level when switching to isometric
            if (this.previousCamera instanceof THREE.OrthographicCamera) {
                this.camera.zoom = this.previousCamera.zoom;
            } else {
                // Set a reasonable default zoom when switching from perspective
                this.camera.zoom = 1;
            }
            this.camera.updateProjectionMatrix();
        }

        this.camera.up.set(0, 0, 1);

        // Update controls to use new camera
        this.controls.object = this.camera;
        this.controls.target.copy(this.previousControlsTarget);
        this.controls.update();

        // Update viewport gizmo
        if (viewportGizmo) {
            viewportGizmo.camera = this.camera;
            viewportGizmo.updateMatrix();
        }

        if (project) {
            project.sceneSettings.iso = true;
        }
    }

    updateAspect(width: number, height: number) {
        if (this.camera instanceof THREE.PerspectiveCamera) {
            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        } else if (this.camera instanceof THREE.OrthographicCamera) {
            // Handle isometric view if active
            const aspect = width / height;
            const currentZoom = this.camera.zoom;

            // Store initial size only once when first switching to orthographic
            if (this.initialFrustumSize === null) {
                this.initialFrustumSize = this.camera.top - this.camera.bottom;
            }

            // Always use the initial size for calculations
            const size = this.initialFrustumSize;

            // Update all frustum planes using the initial size
            this.camera.left = -size * aspect / 2;
            this.camera.right = size * aspect / 2;
            this.camera.top = size / 2;
            this.camera.bottom = -size / 2;

            this.camera.zoom = currentZoom;
            this.camera.updateProjectionMatrix();
        }
        this.renderer.setSize(width, height);
    }

    update(delta: number) {
        this.controls.update(delta);
    }
}